import React, { useState, useEffect, useRef } from 'react';
import { X, Image, Loader2, Check, RefreshCw } from 'lucide-react';
import { apiClient } from '../services/api';
import { logCardReview } from '../utils/reviewUtils';
import { trackAIGenerated, trackImageSaved } from '../utils/analytics';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';

interface GenerateImageModalProps {
  cardId: number;
  cardName: string;
  onClose: () => void;
  onSaved: () => void;
}

export const GenerateImageModal: React.FC<GenerateImageModalProps> = ({ cardId, cardName, onClose, onSaved }) => {
  useBodyScrollLock();
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const progressInterval = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (isGenerating) {
      setProgress(0);
      const DURATION = 20000;
      const TICK = 200;
      let elapsed = 0;
      progressInterval.current = setInterval(() => {
        elapsed += TICK;
        // Ease toward 90% over DURATION, never reach 100% until done
        setProgress(Math.min(90, (elapsed / DURATION) * 100));
      }, TICK);
    } else {
      if (progressInterval.current) clearInterval(progressInterval.current);
      setProgress(100);
    }
    return () => { if (progressInterval.current) clearInterval(progressInterval.current); };
  }, [isGenerating]);
  const [isSaving, setIsSaving] = useState(false);
  const [imageB64, setImageB64] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGenerate = async () => {
    setIsGenerating(true);
    setError(null);
    setSaved(false);
    setImageB64(null);
    try {
      const data = await apiClient.generateImage(cardId, { prompt: prompt.trim() || undefined });
      setImageB64(data.image_b64);
      logCardReview(cardId);
      trackAIGenerated('image', cardId);
    } catch {
      setError('Could not generate the image. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!imageB64) return;
    setIsSaving(true);
    setError(null);
    try {
      await apiClient.saveImage(cardId, imageB64);
      trackImageSaved(cardId);
      onSaved();
      onClose();
    } catch {
      setError('Could not save the image. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-6">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-lg flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4 flex-shrink-0 border-b border-gray-100">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Generate Image</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              <span className="font-medium text-gray-700">&quot;{cardName}&quot;</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Style hint input */}
          <input
            type="text"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !isGenerating && !imageB64) handleGenerate(); }}
            placeholder='e.g. "black and white illustration", "in a natural environment"'
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500/70 focus:border-rose-300 bg-white text-sm"
            disabled={isGenerating || isSaving}
          />

          {/* Initial generate button */}
          {!imageB64 && !isGenerating && (
            <button
              type="button"
              onClick={handleGenerate}
              className="w-full px-4 py-3 text-[15px] font-medium bg-rose-500 hover:bg-rose-600 text-white rounded-lg flex items-center justify-center gap-2 transition-colors"
            >
              <Image className="h-5 w-5" />
              Generate
            </button>
          )}

          {/* Loading state */}
          {isGenerating && (
            <div className="relative w-full aspect-square rounded-xl overflow-hidden bg-gray-100 animate-pulse">
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                <div className="p-3 rounded-full bg-white/70">
                  <Loader2 className="h-8 w-8 text-rose-400 animate-spin" />
                </div>
                <p className="text-sm font-medium text-gray-500">Generating image…</p>
                <p className="text-xs text-gray-400">This usually takes around 20 seconds</p>
                <div className="w-40 bg-gray-200 rounded-full h-1.5 overflow-hidden">
                  <div
                    className="bg-rose-400 h-1.5 rounded-full transition-all duration-200"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Generated image */}
          {imageB64 && !isGenerating && (
            <div className="space-y-4">
              <img
                src={`data:image/png;base64,${imageB64}`}
                alt={`Generated image for "${cardName}"`}
                className="w-full rounded-xl object-cover shadow-sm"
              />

              {saved && (
                <p className="text-xs text-green-600 flex items-center gap-1">
                  <Check className="h-3.5 w-3.5" /> Image saved to card
                </p>
              )}

              {error && (
                <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">{error}</div>
              )}

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleGenerate}
                  disabled={isSaving}
                  className="flex-1 px-3 py-2.5 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
                >
                  <RefreshCw className="h-4 w-4" />
                  Regenerate
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={isSaving || saved}
                  className="flex-1 px-3 py-2.5 text-sm font-medium bg-rose-500 hover:bg-rose-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
                >
                  {isSaving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : saved ? (
                    <><Check className="h-4 w-4" /> Saved</>
                  ) : (
                    'Save to card'
                  )}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isSaving}
                  className="flex-1 px-3 py-2.5 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          )}

          {/* Error before any image */}
          {error && !imageB64 && (
            <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">{error}</div>
          )}
        </div>
      </div>
    </div>
  );
};
